import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Session } from '@supabase/supabase-js';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Navigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  planForkHireRouteWarningModeShadow,
  type LegacyRouteReadinessState,
} from '@/lib/portalIdentityEnforcementPolicy';
import {
  prepareForkHireEmailPasswordAccount,
  type ForkHireEmailProvisioningClient,
} from '@/lib/forkhireEmailProvisioning';
import { isMissingOrganizationLifecycleColumnError } from '@/lib/forkhire/organizationLifecycle';
import { getForkHireProtectedBootstrapRedirect } from '@/lib/forkhire/protectedRouteBootstrap';

type GoogleReadinessState = 'idle' | 'checking' | 'ready' | 'setup_required' | 'blocked';
type BootstrapStatus = 'idle' | 'running' | 'succeeded' | 'failed';

type HireRoleRow = {
  org_id: string | null;
  role: string | null;
};

type OrgLifecycleState = 'idle' | 'checking' | 'active' | 'blocked';

const HIRE_ROLES = [
  'org_owner',
  'org_admin',
  'finance',
  'recruiter',
  'hiring_manager',
  'viewer',
  'talent_coordinator',
  'finance_offer_approver',
  'interview_reviewer',
] as const;
const HIRE_ROLE_SET: ReadonlySet<string> = new Set(HIRE_ROLES);

function getMetadataString(value: unknown) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function hasGoogleProvider(session: Session) {
  const appMetadata = session.user.app_metadata ?? {};
  const provider = getMetadataString(appMetadata.provider);
  const providers = Array.isArray(appMetadata.providers)
    ? appMetadata.providers.map((item) => getMetadataString(item))
    : [];
  const identities = Array.isArray(session.user.identities) ? session.user.identities : [];

  return (
    provider === 'google' ||
    providers.includes('google') ||
    identities.some((identity) => getMetadataString(identity?.provider) === 'google')
  );
}

function isForkHireGoogleSetupSession(session: Session | null) {
  if (!session?.user) return false;

  const metadata = session.user.user_metadata ?? {};
  const visibleEmail = getMetadataString(metadata.visible_email);

  return (
    getMetadataString(metadata.portal) === 'hire' &&
    getMetadataString(metadata.user_type) === 'employer' &&
    Boolean(visibleEmail) &&
    !visibleEmail.includes('auth.fork.internal') &&
    hasGoogleProvider(session)
  );
}

function getForkHireLegacyRouteReadiness({
  loading,
  session,
  profile,
  isGoogleSetupSession,
  googleReadiness,
  pathname,
}: {
  loading: boolean;
  session: Session | null;
  profile: { user_type?: string | null; status?: string | null; org_id?: string | null } | null;
  isGoogleSetupSession: boolean;
  googleReadiness: GoogleReadinessState;
  pathname: string;
}): LegacyRouteReadinessState {
  if (loading || (isGoogleSetupSession && (googleReadiness === 'idle' || googleReadiness === 'checking'))) {
    return 'legacy_loading';
  }

  if (!session) {
    return 'legacy_redirects';
  }

  if (isGoogleSetupSession && googleReadiness === 'blocked') {
    return 'legacy_blocks';
  }

  if (isGoogleSetupSession && googleReadiness === 'setup_required') {
    return 'legacy_redirects';
  }

  if (isGoogleSetupSession && googleReadiness === 'ready' && !profile) {
    return 'legacy_blocks';
  }

  if (!profile) {
    return 'legacy_loading';
  }

  if (
    profile.user_type === 'candidate' ||
    (Boolean(session.user?.user_metadata?.must_reset_password) && pathname !== '/force-reset-password') ||
    (!profile.org_id && pathname !== '/organization-setup')
  ) {
    return 'legacy_redirects';
  }

  if (profile.status === 'deactivated' || profile.status === 'suspended') {
    return 'legacy_blocks';
  }

  return 'legacy_allows';
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const {
    session,
    loading,
    profile,
    user,
    signOut,
    portalIdentityDiagnostic,
    portalIdentityLoading,
    refreshProfile,
  } = useAuth();
  const location = useLocation();
  const [googleReadiness, setGoogleReadiness] = useState<GoogleReadinessState>('idle');
  const [orgLifecycleState, setOrgLifecycleState] = useState<OrgLifecycleState>('idle');
  const [bootstrapStatus, setBootstrapStatus] = useState<BootstrapStatus>('idle');
  const [bootstrapRedirectTo, setBootstrapRedirectTo] = useState<string | null>(null);

  const isGoogleSetupSession = isForkHireGoogleSetupSession(session);
  const forkHireWarningModeShadowPlan = planForkHireRouteWarningModeShadow({
    legacyRouteReadiness: getForkHireLegacyRouteReadiness({
      loading,
      session,
      profile,
      isGoogleSetupSession,
      googleReadiness,
      pathname: location.pathname,
    }),
    diagnostic: portalIdentityLoading ? null : portalIdentityDiagnostic,
  });
  void forkHireWarningModeShadowPlan;

  useEffect(() => {
    if (!isGoogleSetupSession || !session?.user?.id) {
      setGoogleReadiness('idle');
      return;
    }

    let cancelled = false;

    const checkForkHireGoogleReadiness = async () => {
      setGoogleReadiness('checking');

      const { data: dbProfile, error: profileError } = await supabase
        .from('hire_profiles')
        .select('org_id')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (cancelled) return;

      if (profileError) {
        setGoogleReadiness('blocked');
        return;
      }

      if (!dbProfile?.org_id) {
        setGoogleReadiness('setup_required');
        return;
      }

      const { data: roleRows, error: roleError } = await supabase
        .from('hire_user_roles')
        .select('role, org_id')
        .eq('user_id', session.user.id)
        .in('role', HIRE_ROLES);

      if (cancelled) return;

      if (roleError) {
        setGoogleReadiness('blocked');
        return;
      }

      const rows = (roleRows ?? []) as HireRoleRow[];
      const hasRoleOrgCorruption = rows.some((row) => !row.org_id || row.org_id !== dbProfile.org_id);
      const hasValidRole = rows.some(
        (row) => row.org_id === dbProfile.org_id && HIRE_ROLE_SET.has(row.role ?? ''),
      );

      setGoogleReadiness(hasRoleOrgCorruption || !hasValidRole ? 'blocked' : 'ready');
    };

    checkForkHireGoogleReadiness();

    return () => {
      cancelled = true;
    };
  }, [isGoogleSetupSession, session?.user?.id]);

  useEffect(() => {
    if (!profile?.org_id) {
      setOrgLifecycleState('idle');
      return;
    }

    let cancelled = false;

    const checkOrgLifecycle = async () => {
      setOrgLifecycleState('checking');

      const { data, error } = await supabase
        .from('organizations')
        .select('lifecycle_status')
        .eq('id', profile.org_id)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        if (isMissingOrganizationLifecycleColumnError(error)) {
          setOrgLifecycleState('active');
          return;
        }

        setOrgLifecycleState('blocked');
        return;
      }

      const orgLifecycleRow = data as { lifecycle_status?: string | null } | null;
      const lifecycleStatus = orgLifecycleRow?.lifecycle_status ?? 'active';
      setOrgLifecycleState(lifecycleStatus === 'active' ? 'active' : 'blocked');
    };

    checkOrgLifecycle();

    return () => {
      cancelled = true;
    };
  }, [profile?.org_id]);

  useEffect(() => {
    const bootstrapAuthenticatedForkHireProfile = async () => {
      if (loading || !session || !user || profile || bootstrapStatus !== 'idle') return;
      if (hasGoogleProvider(session)) return;

      setBootstrapStatus('running');
      setBootstrapRedirectTo(null);

      const provisioning = await prepareForkHireEmailPasswordAccount(
        supabase as unknown as ForkHireEmailProvisioningClient,
        user,
        {
          visibleEmail: user.email,
          fullName: typeof user.user_metadata?.full_name === 'string' ? user.user_metadata.full_name : null,
        },
      );

      if (!provisioning.ok) {
        console.error('forkhire_profile_bootstrap_failed', { code: provisioning.code });
        setBootstrapStatus('failed');
        return;
      }

      await refreshProfile();
      setBootstrapRedirectTo(getForkHireProtectedBootstrapRedirect(provisioning));
      setBootstrapStatus('succeeded');
    };

    void bootstrapAuthenticatedForkHireProfile();
  }, [bootstrapStatus, loading, profile, refreshProfile, session, user]);

  if (loading || bootstrapStatus === 'running') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/hire/auth" replace />;
  }

  if (bootstrapRedirectTo && bootstrapRedirectTo !== location.pathname) {
    return <Navigate to={bootstrapRedirectTo} replace />;
  }

  if (isGoogleSetupSession && (googleReadiness === 'idle' || googleReadiness === 'checking')) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isGoogleSetupSession && googleReadiness === 'setup_required') {
    return <Navigate to="/hire/complete-company-setup" replace />;
  }

  if (isGoogleSetupSession && googleReadiness === 'blocked') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8 text-center">
        <div className="max-w-md space-y-4">
          <div>
            <h2 className="text-xl font-semibold mb-2">{t('hire.protectedRoute.companySetupNotCompleted')}</h2>
            <p className="text-muted-foreground">
              {t('hire.protectedRoute.companySetupNotCompletedDescription')}
            </p>
          </div>
          <Button onClick={() => void signOut()}>{t('hire.protectedRoute.backToSignIn')}</Button>
        </div>
      </div>
    );
  }

  if (isGoogleSetupSession && googleReadiness === 'ready' && !profile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8 text-center">
        <div className="max-w-md space-y-4">
          <div>
            <h2 className="text-xl font-semibold mb-2">{t('hire.protectedRoute.employerAccountReady')}</h2>
            <p className="text-muted-foreground">
              {t('hire.protectedRoute.employerAccountReadyDescription')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
            <Button variant="outline" onClick={() => window.location.reload()}>{t('common.retry')}</Button>
            <Button onClick={() => void signOut()}>{t('hire.protectedRoute.backToSignIn')}</Button>
          </div>
        </div>
      </div>
    );
  }

  // Wait for profile to load for regular email/password ForkHire sessions.
  if (!profile) {
    if (bootstrapStatus === 'failed' || bootstrapStatus === 'succeeded') {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-8 text-center">
          <div className="max-w-md space-y-4">
            <div>
              <h2 className="text-xl font-semibold mb-2">{t('hire.protectedRoute.accountSetupNeedsAttention')}</h2>
              <p className="text-muted-foreground">
                {t('hire.protectedRoute.accountSetupNeedsAttentionDescription')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setBootstrapRedirectTo(null);
                  setBootstrapStatus('idle');
                }}
              >
                {t('common.retry')}
              </Button>
              <Button onClick={() => void signOut()}>{t('hire.protectedRoute.backToSignIn')}</Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Block candidates from employer routes
  if (profile.user_type === 'candidate') {
    return <Navigate to="/seek" replace />;
  }

  // Block deactivated or suspended hire users
  if (profile.status === 'deactivated' || profile.status === 'suspended') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8 text-center">
        <div>
          <h2 className="text-xl font-semibold mb-2">{t('hire.protectedRoute.accountAccessRestricted')}</h2>
          <p className="text-muted-foreground">{t('hire.protectedRoute.accountAccessRestrictedDescription')}</p>
        </div>
      </div>
    );
  }

  if (profile.org_id && orgLifecycleState === 'checking') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (profile.org_id && orgLifecycleState === 'blocked') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8 text-center">
        <div className="max-w-md">
          <h2 className="text-xl font-semibold mb-2">{t('hire.protectedRoute.organizationAccessRestricted')}</h2>
          <p className="text-muted-foreground">
            {t('hire.protectedRoute.organizationAccessRestrictedDescription')}
          </p>
        </div>
      </div>
    );
  }

  // Force password reset for invited users with temp password
  if (
    session.user?.user_metadata?.must_reset_password &&
    location.pathname !== '/force-reset-password'
  ) {
    return <Navigate to="/force-reset-password" replace />;
  }

  if (!profile.org_id && location.pathname !== '/organization-setup') {
    return <Navigate to="/organization-setup" replace />;
  }

  return <>{children}</>;
}
